// Right-rail section listing the project's garniturs (cabinet sets).
//
// A garnitur is the cabinetry equivalent of a "kitchen run" — base/wall
// units along one wall, an island, or a tall pantry column. Cabinets
// belong to exactly one garnitur; the BOM, cutlist, and CNC export use
// that grouping for shop-floor batching.
//
// This panel is the "current selection" target for cabinet adds: the
// highlighted row is the garnitur a new cabinet from the palette will
// land in.

import { useState } from "react";
import type { Garnitur, GarniturType, Id } from "@/lib/types";

const TYPE_LABEL: Record<GarniturType, string> = {
  upper:        "Upper",
  lower:        "Lower",
  tall:         "Tall",
  island:       "Island",
  freestanding: "Free",
};

/** Default badge color per type — overridden if Garnitur.color is set. */
const TYPE_COLOR: Record<GarniturType, string> = {
  upper:        "#7aa6d6",
  lower:        "#d89c5a",
  tall:         "#9a78c8",
  island:       "#6ec48b",
  freestanding: "#9aa3ad",
};

interface Props {
  garniturs: Garnitur[];
  /** Map of garnitur _id → cabinet count, for the right-aligned counter. */
  cabinetCounts: Record<Id, number>;
  currentId: Id | null;
  onSelect: (id: Id) => void;
  onAdd: (name: string, type: GarniturType) => void;
  onDelete: (id: Id) => void;
}

export function GarnitursPanel({
  garniturs,
  cabinetCounts,
  currentId,
  onSelect,
  onAdd,
  onDelete,
}: Props) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState<GarniturType>("lower");

  function submitAdd() {
    const n = name.trim();
    if (!n) return;
    onAdd(n, type);
    setName("");
    setType("lower");
    setAdding(false);
  }

  return (
    <div style={{ padding: 16, borderBottom: "1px solid var(--line)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span className="label">GARNITURS</span>
        {!adding && (
          <button
            className="ghost"
            style={{ fontSize: 11, padding: "2px 8px" }}
            onClick={() => setAdding(true)}
          >+ Add</button>
        )}
      </div>

      {garniturs.length === 0 && !adding && (
        <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
          No garniturs yet. The first cabinet you add will create one.
        </div>
      )}

      <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "flex", flexDirection: "column", gap: 4 }}>
        {garniturs.map((g) => {
          const color = g.color ?? TYPE_COLOR[g.type];
          const active = g._id === currentId;
          const count = cabinetCounts[g._id] ?? 0;
          return (
            <li key={g._id}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => onSelect(g._id)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onSelect(g._id); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "6px 8px",
                  borderRadius: 4,
                  cursor: "pointer",
                  background: active ? "rgba(255,170,85,0.10)" : "transparent",
                  border: `1px solid ${active ? "var(--accent, #ffaa55)" : "transparent"}`,
                  fontSize: 12,
                }}
              >
                <span
                  aria-hidden
                  style={{ width: 10, height: 10, borderRadius: 5, background: color, flexShrink: 0 }}
                />
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.name}</span>
                <span
                  className="muted"
                  style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4 }}
                  title={TYPE_LABEL[g.type]}
                >{TYPE_LABEL[g.type]}</span>
                <span className="muted" style={{ fontSize: 11, minWidth: 18, textAlign: "right" }}>{count}</span>
                <button
                  className="ghost"
                  style={{ fontSize: 10, padding: "0 4px", color: "var(--fg-dim)" }}
                  title={count > 0 ? "Empty the garnitur before deleting it" : "Delete garnitur"}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (count > 0) {
                      window.alert(`"${g.name}" still has ${count} cabinet${count === 1 ? "" : "s"}. Move or delete them first.`);
                      return;
                    }
                    if (window.confirm(`Delete garnitur "${g.name}"?`)) onDelete(g._id);
                  }}
                >×</button>
              </div>
            </li>
          );
        })}
      </ul>

      {adding && (
        <div style={{ marginTop: 8, padding: 8, borderRadius: 4, background: "rgba(255,255,255,0.03)", display: "flex", flexDirection: "column", gap: 6 }}>
          <input
            placeholder="Name (e.g. Upper run)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") submitAdd();
              if (e.key === "Escape") { setAdding(false); setName(""); }
            }}
            style={{ fontSize: 12 }}
          />
          <select value={type} onChange={(e) => setType(e.target.value as GarniturType)} style={{ fontSize: 12 }}>
            {(Object.keys(TYPE_LABEL) as GarniturType[]).map((t) => (
              <option key={t} value={t}>{TYPE_LABEL[t]}</option>
            ))}
          </select>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={submitAdd} disabled={!name.trim()} style={{ flex: 1, fontSize: 12 }}>Create</button>
            <button className="ghost" onClick={() => { setAdding(false); setName(""); }} style={{ flex: 1, fontSize: 12 }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
